"use client";

import { useState, useEffect, type ReactNode } from "react";
import Link from "next/link";
import Image from "next/image";
import { Menu, LogIn, UserPlus, LogOut, Newspaper } from "lucide-react";
import { SvgMaskIcon } from "@/components/common/svg-mask-icon";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SearchBar } from "@/components/common/search-bar";
import { useIsMobile } from "@/hooks";
import { cn } from "@/lib/utils";
import { createClient } from "@/lib/supabase/client";
import type { User as SupabaseUser } from "@supabase/supabase-js";

interface HeaderProps {
  className?: string;
  showSearchBar?: boolean;
  /** Ana sayfa hero'su gibi tam-kenar görselin ÜSTÜNE binen şeffaf header modu.
   *  En üstte (hero görünürken) şeffaf + beyaz logo; scroll'da katı beyaz + mavi logo. */
  overlay?: boolean;
  /** Header'daki arama barının yanında ek içerik (ör. arama sonuçları sıralama
   *  butonu). Yalnız showSearchBar true iken görünür. */
  searchExtra?: ReactNode;
}

export function Header({
  className,
  showSearchBar = false,
  overlay = false,
  searchExtra,
}: HeaderProps) {
  const isMobile = useIsMobile();
  const [user, setUser] = useState<SupabaseUser | null>(null);
  // headerSearchVisible mantığı: showSearchBar=false ise zaten gizli kalır.
  // showSearchBar=true + sentinel yok ise header search görünür (default true).
  // Sentinel varsa IntersectionObserver observe() çağrıldığı anda ilk callback'i
  // tetikler, bu sayede ayrı bir initial setState'e gerek kalmaz — React 19
  // set-state-in-effect kuralını ihlal etmez.
  const [headerSearchVisible, setHeaderSearchVisible] = useState(!overlay);

  useEffect(() => {
    if (!showSearchBar) return;

    const sentinel = document.querySelector("[data-hero-searchbar]");
    if (!sentinel) return; // sentinel yoksa default true kullanılır

    const observer = new IntersectionObserver(
      ([entry]) => setHeaderSearchVisible(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [showSearchBar]);

  useEffect(() => {
    const supabase = createClient();
    if (!supabase) return;

    const getUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setUser(user);
    };

    getUser();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    const supabase = createClient();
    if (supabase) {
      await supabase.auth.signOut();
      window.location.href = "/";
    }
  };

  if (isMobile) return null;

  // overlay modunda: en üstte (hero search görünürken) şeffaf; scroll sonrası katı.
  const transparent = overlay && !headerSearchVisible;

  return (
    <header
      className={cn(
        "sticky top-0 z-50 w-full transition-colors duration-300",
        transparent ? "bg-transparent" : "bg-white",
        overlay && !transparent && "shadow-sm",
        className
      )}
    >
      <div
        className={cn(
          "mx-auto flex h-20 max-w-7xl items-center justify-between px-4 transition-transform duration-300 sm:px-6 lg:px-8",
          // Hero'da EN ÜSTTE (şeffaf) logo/butonlar biraz aşağı — ama header YÜKSELMEDEN.
          // translate layout'u etkilemez → scroll'da beliren arama-barlı header ESKİ boyutunda kalır.
          transparent && "translate-y-7"
        )}
      >
        <Link href="/" className="flex shrink-0 items-center" aria-label="konakla ana sayfa">
          {/* Hero'da (şeffaf) logo BÜYÜK — header kutusu (h-20) dışına, görselin
                üstüne taşar; layout değişmez. Scroll'da katı header'da eski boyut (h-14). */}
          <Image
            src={transparent ? "/logo_beyaz.png" : "/logo_mavi.png"}
            alt=""
            width={300}
            height={90}
            className={cn("w-auto transition-all duration-300", transparent ? "h-16" : "h-14")}
            priority
          />
        </Link>

        {showSearchBar && (
          <div
            className={cn(
              "mx-6 flex flex-1 items-center justify-center gap-2 transition-all duration-300",
              headerSearchVisible
                ? "translate-y-0 opacity-100"
                : "pointer-events-none -translate-y-2 opacity-0"
            )}
          >
            <SearchBar variant="compact" />
            {searchExtra}
          </div>
        )}

        <div className="flex shrink-0 items-center gap-4">
          <Link href="/for-hosts">
            <Button
              variant="ghost"
              className={cn(
                "rounded-full border text-sm font-medium",
                transparent
                  ? "border-white/60 text-white hover:bg-white/10 hover:text-white"
                  : "border-border text-foreground hover:border-primary hover:text-primary"
              )}
            >
              Evinizi Listeleyin
            </Button>
          </Link>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className={cn(
                  "h-10 w-10 rounded-full",
                  transparent
                    ? "border-white/50 bg-transparent text-white hover:bg-white/10 hover:text-white"
                    : "border-border"
                )}
              >
                <Menu className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 rounded-xl p-1.5">
              {user ? (
                <>
                  <DropdownMenuItem asChild>
                    <Link href="/profile" className="flex items-center gap-3">
                      <SvgMaskIcon src="/profil.svg" className="text-muted-foreground h-4 w-4" />
                      <span>Profilim</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/favorites" className="flex items-center gap-3">
                      <SvgMaskIcon src="/favoriler.svg" className="text-muted-foreground h-4 w-4" />
                      <span>Favoriler</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/for-hosts" className="flex items-center gap-3">
                      <SvgMaskIcon src="/ekle.svg" className="text-muted-foreground h-4 w-4" />
                      <span>Evinizi Listeleyin</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/about" className="flex items-center gap-3">
                      <SvgMaskIcon src="/anasayfa.svg" className="text-muted-foreground h-4 w-4" />
                      <span>Hakkımızda</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/blog" className="flex items-center gap-3">
                      <Newspaper className="text-muted-foreground h-4 w-4" />
                      <span>Blog</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleSignOut}
                    className="text-destructive flex items-center gap-3"
                  >
                    <LogOut className="h-4 w-4" />
                    <span>Çıkış Yap</span>
                  </DropdownMenuItem>
                </>
              ) : (
                <>
                  <DropdownMenuItem asChild>
                    <Link href="/auth" className="flex items-center gap-3">
                      <LogIn className="text-muted-foreground h-4 w-4" />
                      <span>Giriş Yap</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/auth" className="flex items-center gap-3">
                      <UserPlus className="text-muted-foreground h-4 w-4" />
                      <span>Kayıt Ol</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/favorites" className="flex items-center gap-3">
                      <SvgMaskIcon src="/favoriler.svg" className="text-muted-foreground h-4 w-4" />
                      <span>Favoriler</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/for-hosts" className="flex items-center gap-3">
                      <SvgMaskIcon src="/ekle.svg" className="text-muted-foreground h-4 w-4" />
                      <span>Evinizi Listeleyin</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/about" className="flex items-center gap-3">
                      <SvgMaskIcon src="/anasayfa.svg" className="text-muted-foreground h-4 w-4" />
                      <span>Hakkımızda</span>
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link href="/blog" className="flex items-center gap-3">
                      <Newspaper className="text-muted-foreground h-4 w-4" />
                      <span>Blog</span>
                    </Link>
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
